/**
 * composer-mount.ts — Postprocessing chain mount point.
 *
 * Plan 10 scope: render passes directly through to WebGLRenderer with no
 * intermediate effects when no PostprocessingConfig is supplied.
 *
 * Plan 14a: when a PostprocessingConfig is provided, delegates to
 * createPostprocessingChain() which wires an EffectComposer pipeline
 * (RenderPass → BloomEffect → SMAAEffect → ChromaticAberrationEffect →
 * LUT3DEffect) via pmndrs/postprocessing.
 *
 * The `attachPass` method throws because the chain is fully configured at
 * construction time; arbitrary pass injection after construction is not
 * supported (would bypass the controlled effect ordering in Task 9.1).
 */

import * as THREE from 'three';
import type { ComposerMount } from './types';
import type { PostprocessingConfig } from './vfx/types';
import { createPostprocessingChain } from './vfx/postprocessing-chain';

export interface ComposerMountConfig {
  readonly renderer: THREE.WebGLRenderer;
  /** Optional postprocessing chain config (Plan 14a).
   *  When omitted the mount falls through directly to renderer.render(). */
  readonly postprocessing?: PostprocessingConfig;
}

function canUsePostprocessing(renderer: THREE.WebGLRenderer): boolean {
  const candidate = renderer as unknown as Record<string, unknown>;
  return (
    typeof candidate['getSize'] === 'function' &&
    typeof candidate['getContext'] === 'function' &&
    typeof candidate['setRenderTarget'] === 'function'
  );
}

export function createComposerMount(
  config: ComposerMountConfig,
  /** Internal override for scene/camera — used when a PostprocessingConfig
   *  is supplied so the RenderPass is wired up correctly.  Pass-through mode
   *  ignores these; the render() call receives live scene+camera per frame. */
  scene?: THREE.Scene,
  camera?: THREE.Camera,
): ComposerMount {
  const { renderer, postprocessing } = config;

  // ── Postprocessing path ───────────────────────────────────────────────────
  if (postprocessing != null && scene != null && camera != null && canUsePostprocessing(renderer)) {
    return createPostprocessingChain(renderer, scene, camera, postprocessing);
  }

  // ── Pass-through path (Plan 10 behaviour) ─────────────────────────────────
  let _width = 0;
  let _height = 0;
  let _disposed = false;

  return {
    render(s: THREE.Scene, c: THREE.Camera): void {
      renderer.render(s, c);
    },

    attachPass(_pass: unknown): never {
      throw new Error(
        'ComposerMount.attachPass: chain is fully configured at construction time; ' +
          'use PostprocessingConfig options to enable effects',
      );
    },

    onResize(width: number, height: number): void {
      _width = width;
      _height = height;
    },

    dispose(): void {
      if (_disposed) return;
      _disposed = true;
      void _width;
      void _height;
    },
  };
}
